import { createSelector } from "@reduxjs/toolkit";
import { companyApplicationApi, selectLifePlans } from "EmployerApplication/api";
import { type EntityTable } from "EntitiesApi/Reducers";
import isIntuit from "Intuit/isIntuit";
import { identity, values, every, filter } from "lodash";
import get from "lodash/get";
import { formValueSelector } from "redux-form";
import { sortAlphabeticallyBy } from "Utils/selectors";

import { getSelectedMedicalPlans, getSelectedDentalPlan, getSelectedVisionPlan } from "Common/CartService";
import { formParseDate } from "Common/forms/utils";

import {
  renewalCoverageOptionsURL,
  renewalOpenEnrollmentURL,
  renewalDocumentUploadURL,
  renewalEmployeeStatusURL,
  renewalDoneURL,
  renewalCompanyInformationURL,
} from "./urls";

import type {
  EmployerRenewalCompanyProfile,
  EmployerRenewalCompanyPolicy,
  EmployerRenewalEmployeeForm,
  Cart,
  MedicalPlan,
  CompanyContribution,
} from "Utils/types";

const selector = formValueSelector("employerApplicationForm");
const dateEstablishedAt = (state) => selector(state, "company_profile[date_established_at]");

export const getShowPayrollStartDate = createSelector(dateEstablishedAt, (date) => {
  let establishedWithinPastYear = false;
  if (date) {
    const comparisonDate = new Date();
    comparisonDate.setFullYear(comparisonDate.getFullYear() - 1);
    const dateEstablishedAtDate = formParseDate(date);
    establishedWithinPastYear = dateEstablishedAtDate > comparisonDate;
  }
  return establishedWithinPastYear;
});

export const getCart = (state: State): Cart | null | undefined => state.cart;

export const employerApplicationFormPage = (state) => get(state, "form.employerApplicationForm.page");

export const hasMultipleMedicalPlansOffered = (state) => state.cart.medical.length > 1;

const selectCompanyApplication = companyApplicationApi.endpoints.getCurrentCompanyApplication.select();

export const getCompanyApplication = createSelector(selectCompanyApplication, (companyApplicationResult) => companyApplicationResult?.data);

export const getCompanyProfileIDToken = createSelector(getCompanyApplication, (companyApplication) => companyApplication?.companyIdToken);

export const getCompanyZipCode = createSelector(getCompanyApplication, (companyApplication) => companyApplication?.zip_code_number);

export const showWageDocumentPicker = createSelector(
  getCompanyApplication,
  (companyApplication) => isIntuit() && ["CA", "TX", "FL", "AZ", "WA", "VA", "NC", "IL", "GA", "CO", "NJ", "MI"].includes(companyApplication?.quoteState),
);

export const getEligibleEmployees = (state) => filter(state.employees, (e) => e.is_eligible);

export const getEmployees = createSelector([identity], (state) => values(state.employees).sort(sortAlphabeticallyBy("full_name")) || []);

export const canLinkAccount = createSelector([getEmployees], (employees) => every(employees, { linked_account: false }));

export const getSelectedLifePlan = createSelector([selectLifePlans, getCart], (lifePlansResult, cart) => {
  const lifePlans = lifePlansResult.data?.results ?? [];
  return lifePlans.find((lp) => lp.id == cart.life);
});

export const getAllSelectedPlans = createSelector(
  [getSelectedMedicalPlans, getSelectedDentalPlan, getSelectedVisionPlan, getSelectedLifePlan],

  (medicalPlans, dentalPlan, visionPlan, lifePlan) => ({
    medicalPlans,
    dentalPlan,
    visionPlan,
    lifePlan,
  }),
);

export type State = {
  companyProfileId: string;
  companyPolicyId: string;
  currentlyEnrolledCompanyPolicyId: string;
  entities: {
    companyProfiles: EntityTable<EmployerRenewalCompanyProfile>;
    companyPolicies: EntityTable<EmployerRenewalCompanyPolicy>;
    employeeForms: EntityTable<EmployerRenewalEmployeeForm>;
    companyContributions: EntityTable<CompanyContribution>;
    medicalPlans: EntityTable<MedicalPlan>;
  };
};

// TODO-SI-7651 - replace with selector from entities api
export const getCompanyProfile = (s: State): EmployerRenewalCompanyProfile | null | undefined => s.entities.companyProfiles[s.companyProfileId];

// TODO-SI-7651 - replace with selector from entities api
export const getCompanyPolicy = (s: State): EmployerRenewalCompanyPolicy | null | undefined => s.entities.companyPolicies[s.companyPolicyId];

export const getCurrentlyEnrolledCompanyPolicy = (state: State): EmployerRenewalCompanyPolicy | null | undefined =>
  state.entities.companyPolicies[state.currentlyEnrolledCompanyPolicyId];

export const getMedicalPlanById = (state: State, planId: string): MedicalPlan | null | undefined => state.entities.medicalPlans[planId];

export const getEmployeeForms = (s: State): EmployerRenewalEmployeeForm[] => {
  const company = getCompanyProfile(s);
  return company && company.employeeForms ? company.employeeForms.map((id) => s.entities.employeeForms[id]) : [];
};

export const getAnyCurrentEmployeePolicyNotActivated = (s: State): boolean => {
  const employeeForms = getEmployeeForms(s);
  return employeeForms
    .filter((x) => !x.renewalStatus)
    .some(
      (employeeForm: EmployerRenewalEmployeeForm) =>
        ["Declined", "Enrolled", "In Progress", "Not Started", "Ineligible"].indexOf(employeeForm.currentStatus) >= 0,
    );
};

export const getAnyRenewalEmployeePolicyNotActivated = (s: State): boolean => {
  const employeeForms = getEmployeeForms(s);
  return employeeForms
    .filter((x) => !x.renewalStatus)
    .some(
      (employeeForm: EmployerRenewalEmployeeForm) =>
        ["Declined", "Done", "Not Started", "In Progress", "Incomplete Application", "Ineligible"].indexOf(employeeForm.renewalStatus) >= 0,
    );
};

export const getAnyEmployeePolicyNotComplete = (s: State): boolean => {
  const employeeForms = getEmployeeForms(s);
  return employeeForms.some((employeeForm: EmployerRenewalEmployeeForm) => ["Not Started", "In Progress"].includes(employeeForm.renewalStatus));
};

type ProcessOverviewData = {
  overview: boolean;
  companyInformation: boolean;
  choosePlan: boolean;
  coverageOptions: boolean;
  sign: boolean;
  documentUpload: boolean;
  openEnrollment: boolean;
  employeeStatus: boolean;
};

export const getProcessOverviewData = createSelector(
  [getCompanyPolicy, getAnyEmployeePolicyNotComplete, getCompanyApplication],
  (companyPolicy, anyEmployeePolicyNotComplete, companyApplication): ProcessOverviewData => ({
    overview: true,
    companyInformation: companyApplication?.requiredFieldsCompleted,
    choosePlan: !!(companyPolicy && companyPolicy.medicalPlanID),
    coverageOptions: !!(companyPolicy && companyPolicy.probationaryPeriod),
    sign: !!(companyPolicy && companyPolicy.signatureImageDataUrl),
    openEnrollment: !!(companyPolicy && (companyPolicy.skipOpenEnrollment === true || companyPolicy.skipOpenEnrollment === false)),

    documentUpload: !!(companyPolicy && companyPolicy.uploadDocsProps.every((file) => file.originalFilename !== null)),

    employeeStatus: !!(!anyEmployeePolicyNotComplete || (companyPolicy && companyPolicy.skipOpenEnrollment === true)),
  }),
);

export const getFirstIncompleteURL = (state: State): string => {
  const processOverview = getProcessOverviewData(state);
  const companyPolicy = getCompanyPolicy(state);
  const changingCarrier = companyPolicy ? companyPolicy.changingCarrier : null;
  if (!processOverview.companyInformation) {
    return renewalCompanyInformationURL;
  }
  if (!processOverview.companyInformation) {
    return renewalCoverageOptionsURL;
  }
  if (!processOverview.coverageOptions || !processOverview.sign) {
    return renewalCoverageOptionsURL;
  }
  if (!processOverview.openEnrollment) {
    return renewalOpenEnrollmentURL;
  }
  if (!processOverview.documentUpload && changingCarrier) {
    return renewalDocumentUploadURL;
  }
  if (!processOverview.employeeStatus) {
    return renewalEmployeeStatusURL;
  }
  return renewalDoneURL;
};

export const getCarrierChanged = (state: State): boolean => {
  const currentPolicy = getCurrentlyEnrolledCompanyPolicy(state);
  const currentMedicalPlan = currentPolicy ? getMedicalPlanById(state, currentPolicy.medicalPlanID) : null;
  const currentMedicalPlanSymbol = currentMedicalPlan ? currentMedicalPlan.plan_brand_symbol : null;

  const renewalPolicy = getCompanyPolicy(state);
  const renewalMedicalPlan = renewalPolicy ? getMedicalPlanById(state, renewalPolicy.medicalPlanID) : null;
  const renewalMedicalPlanSymbol = renewalMedicalPlan ? renewalMedicalPlan.plan_brand_symbol : null;

  const carrierSame = currentMedicalPlanSymbol && renewalMedicalPlanSymbol ? currentMedicalPlanSymbol === renewalMedicalPlanSymbol : false;

  return !carrierSame;
};

export const getPlanCountChanged = (state: State): boolean => {
  const currentPolicy = getCurrentlyEnrolledCompanyPolicy(state);
  const cart = getCart(state);
  const currentOfferedPlanTypes = currentPolicy ? currentPolicy.offeredPlanTypes : null;
  const currentMedicalCount = currentPolicy ? currentPolicy.medicalPlans.length : null;
  const currentDental = currentOfferedPlanTypes ? currentOfferedPlanTypes.includes("dental") : null;
  const currentVision = currentOfferedPlanTypes ? currentOfferedPlanTypes.includes("vision") : null;
  const renewalMedialCount = cart && cart.medical ? cart.medical.length : null;
  const renewalDental = cart ? cart.dental !== null : null;
  const renewalVision = cart ? cart.vision !== null : null;
  const medicalCountSame = currentMedicalCount && renewalMedialCount ? currentMedicalCount === renewalMedialCount : false;
  const dentalCountSame = currentDental === renewalDental;
  const visionCountSame = currentVision === renewalVision;
  const planCountSame = medicalCountSame && dentalCountSame && visionCountSame;

  return !planCountSame;
};

export const defaultNonTieredContribution = {
  employeePercentage: 50,
  dependentPercentage: 0,
  employeeFixedAmount: null,
  dependentFixedAmount: null,
};

export const getNonTieredContribution = (state) => {
  const contributions = state.entities.companyContributions;
  if (contributions === undefined) {
    return null;
  }
  return Object.values(contributions).filter((contribution) => contribution.role === null)[0] || defaultNonTieredContribution;
};
